/**
 * Fachliche Grundtypen des Kochbuchs.
 *
 * Alle Texte sind deutsch, die Bezeichner im Code englisch. Das hält die
 * Oberfläche und die Daten konsistent und macht spätere Erweiterungen einfach.
 */

/** Schwierigkeitsgrad eines Rezepts. */
export type Difficulty = 'easy' | 'medium' | 'hard';

/** Reihenfolge für Sortierung und Anzeige der Schwierigkeitsgrade. */
export const DIFFICULTY_ORDER: readonly Difficulty[] = ['easy', 'medium', 'hard'];

/** Deutsche Bezeichnung eines Schwierigkeitsgrades. */
export const DIFFICULTY_LABELS: Readonly<Record<Difficulty, string>> = {
  easy: 'Leicht',
  medium: 'Mittel',
  hard: 'Schwer',
};

/**
 * Kurze Erläuterung, was ein Schwierigkeitsgrad bedeutet.
 * So stufen alle im Haushalt ein Rezept gleich ein.
 */
export const DIFFICULTY_HINTS: Readonly<Record<Difficulty, string>> = {
  easy: 'Wenige Schritte, keine besonderen Techniken.',
  medium: 'Mehrere Schritte oder eine Technik, die etwas Übung braucht.',
  hard: 'Viele Schritte, Zeitplanung nötig oder anspruchsvolle Techniken.',
};

/** Anzahl der Kochmützen, die ein Grad in der Anzeige bekommt. */
export const DIFFICULTY_HATS: Readonly<Record<Difficulty, number>> = {
  easy: 1,
  medium: 2,
  hard: 3,
};

/** Zeitangaben eines Rezepts, jeweils in Minuten. */
export interface RecipeTimes {
  /** Rüsten und Abwiegen, bevor es losgeht. */
  readonly preparationMinutes: number;
  /** Aktive Arbeit am Herd oder Tisch. */
  readonly activeMinutes: number;
  /** Kochzeit, ohne aktive Arbeit. */
  readonly cookingMinutes: number;
  /** Backzeit im Ofen. */
  readonly bakingMinutes: number;
  /** Gehen, Ziehen, Marinieren. */
  readonly restingMinutes: number;
  /** Kühlen und Festwerden. */
  readonly coolingMinutes: number;
}

/** Ein Rezept ohne Zeitangaben. */
export const EMPTY_TIMES: RecipeTimes = {
  preparationMinutes: 0,
  activeMinutes: 0,
  cookingMinutes: 0,
  bakingMinutes: 0,
  restingMinutes: 0,
  coolingMinutes: 0,
};

/** Fertige Zeitstufen für Filter und Schnellauswahl. */
export interface TimeBucket {
  readonly id: string;
  readonly label: string;
  /** Obergrenze in Minuten, einschließlich. */
  readonly maxMinutes: number;
}

/** Zeitstufen für die Gesamtzeit eines Rezepts. */
export const TOTAL_TIME_BUCKETS: readonly TimeBucket[] = [
  { id: 'up-to-15', label: 'bis 15 Min', maxMinutes: 15 },
  { id: 'up-to-30', label: 'bis 30 Min', maxMinutes: 30 },
  { id: 'up-to-60', label: 'bis 60 Min', maxMinutes: 60 },
  { id: 'over-60', label: 'über 1 Std', maxMinutes: Number.POSITIVE_INFINITY },
];

/** Wie sich die Menge einer Zutat beim Umrechnen der Portionen verhält. */
export type ScalingMode =
  /** Wächst genau mit, zum Beispiel Mehl. */
  | 'linear'
  /** Wird sinnvoll gerundet, zum Beispiel Eier. */
  | 'rounded'
  /** Wächst unterproportional, zum Beispiel Gewürze. */
  | 'sublinear'
  /** Bleibt gleich, zum Beispiel eine Prise Salz oder ein Päckchen Backpulver. */
  | 'fixed';

/** Auf welche Größe sich die Mengen eines Rezepts beziehen. */
export type BaseUnit = 'portions' | 'persons' | 'pieces' | 'pan';

/** Deutsche Bezeichnung der Bezugsgröße. */
export const BASE_UNIT_LABELS: Readonly<Record<BaseUnit, string>> = {
  portions: 'Portionen',
  persons: 'Personen',
  pieces: 'Stück',
  pan: 'Form',
};

/** Bezugsgröße eines Rezepts, zum Beispiel 4 Portionen. */
export interface RecipeYield {
  readonly amount: number;
  readonly unit: BaseUnit;
  /** Zusätzliche Beschreibung, zum Beispiel "Springform 26 cm". */
  readonly detail?: string;
}

/** Bewertung einer Person für ein Rezept. */
export interface RecipeRating {
  readonly userId: string;
  /** Ganze Sterne von 1 bis 5. */
  readonly stars: number;
  readonly comment?: string;
  readonly updatedAt: string;
}

/** Eine einzelne Zutat innerhalb eines Rezepts. */
export interface RecipeIngredient {
  readonly id: string;
  /** Zutatengruppe, zum Beispiel "Für den Teig". */
  readonly group?: string;
  /** Menge in der Bezugsgröße des Rezepts. */
  readonly amount: number | null;
  /** Einheit, zum Beispiel "g" oder "EL". */
  readonly unit: string;
  /** Name der Zutat, zum Beispiel "Mehl Type 405". */
  readonly name: string;
  /** Zusatz wie "zimmerwarm" oder "fein gehackt". */
  readonly note?: string;
  readonly scaling: ScalingMode;
  /** Verweis auf ein Produkt in der Einkaufsliste, falls vorhanden. */
  readonly productId?: string;
}

/** Verweis auf eine prozedurale Animation samt Parametern. */
export interface StepAnimation {
  readonly templateId: string;
  readonly params?: Readonly<Record<string, string | number | boolean>>;
}

/** Ein Arbeitsschritt eines Rezepts. */
export interface RecipeStep {
  readonly id: string;
  /** Abschnitt, zum Beispiel "Teig" oder "Backen". */
  readonly section?: string;
  readonly title: string;
  /** Anleitung. Mengen stehen als Platzhalter, zum Beispiel {mehl}. */
  readonly instruction: string;
  readonly durationMinutes?: number;
  /** Passiv heißt: Es muss nicht danebengestanden werden. */
  readonly passive: boolean;
  readonly timer: boolean;
  /** Temperatur in Grad Celsius. */
  readonly temperatureCelsius?: number;
  /** Verweis auf eine prozedurale Animation, siehe packages/animations. */
  readonly animation?: StepAnimation;
  /** Hilfe für Anfänger. */
  readonly whyItWorks?: string;
  readonly doneWhen?: string;
}

/** Vollständiges Rezept. */
export interface Recipe {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly difficulty: Difficulty;
  readonly baseYield: RecipeYield;
  readonly times: RecipeTimes;
  readonly ingredients: readonly RecipeIngredient[];
  readonly steps: readonly RecipeStep[];
  readonly categoryIds: readonly string[];
  readonly tags: readonly string[];
  readonly imageIds: readonly string[];
  readonly videoIds: readonly string[];
  readonly ratings: readonly RecipeRating[];
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Kategorie eines Rezepts, zum Beispiel "Kuchen & Torten". */
export interface Category {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  /** Kennung eines Piktogramms aus der Icon-Sammlung. */
  readonly icon: string;
  readonly sortOrder: number;
}

/** Schlagwort eines Rezepts, zum Beispiel "vegan". */
export interface Tag {
  readonly id: string;
  readonly name: string;
  /** Vergleichsform für die Zusammenführung gleicher Schreibweisen. */
  readonly normalizedName: string;
}

/** Protokolleintrag: Wer hat wann ein Rezept gekocht. */
export interface CookLogEntry {
  readonly id: string;
  readonly recipeId: string;
  readonly userId: string;
  readonly cookedAt: string;
  /** Anzahl der Portionen, die tatsächlich gekocht wurden. */
  readonly servings: number;
}
